import EntityContactRow              from '@/pages/Entity/components/Rows/EntityContactRow';
import Spinner                       from '@/components/spinner/Spinner';
import { Tooltip }                   from '@/components/ui/tooltip';
import { Button }                    from '@/components/ui/button';
import { HStack }                    from '@chakra-ui/react';
import { PaginationItems }           from '@/components/ui/pagination';
import { PaginationNextTrigger }     from '@/components/ui/pagination';
import { PaginationPrevTrigger }     from '@/components/ui/pagination';
import { PaginationRoot }            from '@/components/ui/pagination';
import ContactModel                  from '@/models/services/network/ContactModel';
import EntityModel                   from '@/models/services/fondations/EntityModel';
import FileModel                     from '@/models/services/file/FileModel';
import RelationRequestModel          from '@/models/services/network/RelationRequestModel';
import RelationRequestStatusModel    from '@/models/services/network/RelationRequestStatusModel';
import RelationContactModel          from '@/models/services/network/RelationContact';
import RelationStatusModel           from '@/models/services/network/RelationStatus';
import useEntityContactsBox          from '@/pages/Entity/hooks/useEntityContactsBox';
import ApiCollection                 from '@widesk-core/models/ApiCollection';
import PagedCollection               from '@widesk-core/models/PagedCollection';
import { Box }                       from '@chakra-ui/react';
import { Text }                      from '@chakra-ui/react';
import React                         from 'react';
import { HiPlus }                    from 'react-icons/hi';
import { observer }                  from 'mobx-react';
import { useTranslation }            from 'react-i18next';
import useFetch                      from '@widesk-core/hooks/useFetch';

const CONTACTS_PER_PAGE = 5;

interface IEntityContactsBoxProps {
	entity:         EntityModel;
	gestionnaireId: string | undefined;
	managerIds:     Set<string>;
	isReadOnly?:    boolean;
	onRolesChange:  () => void;
}

const EntityContactsBox = observer(({
	entity,
	gestionnaireId,
	managerIds,
	isReadOnly,
	onRolesChange,
}: IEntityContactsBoxProps) => {
	const { t } = useTranslation();
	const [page, setPage] = React.useState(1);

	const contactCollection                = React.useMemo(() => new PagedCollection(ContactModel), []);
	const relationRequestCollection        = React.useMemo(() => new ApiCollection(RelationRequestModel), []);
	const relationContactCollection        = React.useMemo(() => new ApiCollection(RelationContactModel), []);
	const relationRequestStatusCollection  = React.useMemo(() => new ApiCollection(RelationRequestStatusModel), []);
	const relationStatusCollection         = React.useMemo(() => new ApiCollection(RelationStatusModel), []);
	const [contactFiles, setContactFiles]  = React.useState<{ contactId: string; file: FileModel }[]>([]);

	const { isLoading, fetchAsync } = useFetch(async () => {
		contactCollection.clear();
		await Promise.all([
			contactCollection
				.setSort('firstname', 'asc')
				.setFilter('ownerUser.userUrn', entity.urn)
				.setItemsPerPage(CONTACTS_PER_PAGE)
				.setPage(page)
				.list(),
			relationRequestStatusCollection.list(),
			relationStatusCollection.list(),
		]);

		const contactIds = contactCollection.map(m => m.id);

		const newContactFiles: { contactId: string; file: FileModel }[] = [];
		await Promise.all([
			Promise.all(
				contactCollection.map(async (contact) => {
					const files = await new ApiCollection(FileModel)
						.setFilters({
							'typeSource.type.reference': 'contact_photo',
							'typeSource.source.entityUrn': [contact.get('@urn')],
						})
						.list();
					const file = files.models[0] as unknown as FileModel;
					if (file) newContactFiles.push({ contactId: String(contact.id), file });
				}),
			).then(() => setContactFiles(newContactFiles)),
			relationRequestCollection.listBy(contactIds, 'contact'),
			relationContactCollection.listBy(contactIds, 'contact'),
		]);

		await Promise.all([
			relationRequestCollection.whenIsLoaded(m => m.relationRequestStatus),
			relationContactCollection.whenIsLoaded(m => m.relation),
		]);
	});

	React.useEffect(() => {
		fetchAsync();
	}, [page]);

	const onSuccess = React.useCallback(() => {
		fetchAsync();
		onRolesChange();
	}, [fetchAsync, onRolesChange]);

	const { modalElement, openAddModal, openEditModal, openDeleteModal, openUnassignModal,
		openSendRelationRequestModal, openCancelRelationRequestModal, openDissociateModal } =
		useEntityContactsBox({ entityUrn: entity.urn, onSuccess, relationRequestStatusCollection, relationStatusCollection });

	const getFile = (contactId: string) =>
		contactFiles.find(cf => cf.contactId === contactId)?.file;

	const getRelationRequest = (contact: ContactModel) =>
		relationRequestCollection.find(m => m.getUrn('contact') === contact.urn) ?? undefined;

	const getRelation = (contact: ContactModel) =>
		relationContactCollection.find(m => m.getUrn('contact') === contact.urn)?.relation ?? undefined;

	const total = contactCollection.total || 0;

	return (
		<>
			{modalElement}

			<Box backgroundColor="white" borderRadius="md" boxShadow="md" padding="20px">
				<Box display="flex" alignItems="center" justifyContent="space-between" mb="15px">
					<Text className="poppins-semibold" fontSize="16px">{t('pages.entity.contacts.title')}</Text>
					<Tooltip content={t('pages.entity.contacts.actions.add.tooltip')} showArrow>
						<AddButton onClick={openAddModal} disabled={isReadOnly} />
					</Tooltip>
				</Box>

				{isLoading ? (
					<Box py="20px">
						<Spinner />
					</Box>
				) : contactCollection.length === 0 ? (
					<Text fontSize="14px" color="gray.400">{t('pages.entity.contacts.empty')}</Text>
				) : (
					<Box display="flex" flexDirection="column">
						{contactCollection.map((contact, i) => {
							const isGestionnaire = String(contact.id) === gestionnaireId;
							const isManager      = managerIds.has(String(contact.id));
							const relationRequest = getRelationRequest(contact);
							const relation        = getRelation(contact);
							return (
								<EntityContactRow
									key={contact.id}
									contact={contact}
									isFirst={i === 0}
									isGestionnaire={isGestionnaire}
									isManager={isManager}
									file={getFile(String(contact.id))}
									relationRequest={relationRequest}
									relation={relation}
									onEdit={() => openEditModal(contact, getFile(String(contact.id)))}
									onDelete={() => openDeleteModal(contact)}
									onUnassign={isManager ? () => openUnassignModal(contact) : undefined}
									onSendRelationRequest={() => openSendRelationRequestModal(contact)}
									onCancelRelationRequest={relationRequest ? () => openCancelRelationRequestModal(contact, relationRequest) : undefined}
									onDissociate={relation ? () => openDissociateModal(contact, relation) : undefined}
									isReadOnly={isReadOnly}
								/>
							);
						})}
					</Box>
				)}

				{total > CONTACTS_PER_PAGE && (
					<Box
						display="flex"
						justifyContent="flex-end"
						borderTop="1px solid"
						borderColor="gray.100"
						pt="20px"
						mt="8px"
					>
						<PaginationRoot
							count={total}
							page={page}
							pageSize={CONTACTS_PER_PAGE}
							variant="solid"
							onPageChange={(change) => setPage(change.page)}
							size="sm"
						>
							<HStack>
								<PaginationPrevTrigger />
								<PaginationItems />
								<PaginationNextTrigger />
							</HStack>
						</PaginationRoot>
					</Box>
				)}
			</Box>
		</>
	);
});

export default EntityContactsBox;

const AddButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
	<Button
		size="sm"
		borderRadius="md"
		backgroundColor="black"
		color="white"
		_hover={{ opacity: '0.8' }}
		onClick={onClick}
		padding="0 8px"
		minWidth="32px"
		height="32px"
		disabled={disabled}
	>
		<HiPlus />
	</Button>
);
